<svelte:options runes={false} />
<script>
  // Integrity tab (TASK-K2T-8): the audit surface — the first consumer that joins NAV, the
  // ledger-derived book, marks, targets, and fills, and asserts they agree. Everything is
  // SERVER-computed (one IntegritySnapshot so every surface shows identical numbers); this tab
  // orders and renders. Read-only by construction: no order entry, no write path. Live wiring
  // mirrors ExecutionTab (NAV-tick + decision-tick hints + 30 s poll; KTD7 — SSE is a hint, the
  // numbers on screen always come from the authoritative REST read). 404 → honest empty state.
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { navTickSeq, decisionTickSeq } from '../../../lib/autotradeStore.js';
  import ChecksStrip from '../integrity/ChecksStrip.svelte';
  import BookDriftTable from '../integrity/BookDriftTable.svelte';
  import RebalanceTicket from '../integrity/RebalanceTicket.svelte';
  import CostLine from '../integrity/CostLine.svelte';
  import CadenceStrip from '../integrity/CadenceStrip.svelte';
  import StopLadder from '../integrity/StopLadder.svelte';

  export let strategy = null;
  export let status = null;

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  let snapshot = null; // IntegritySnapshot
  let loading = false;
  let error = null;
  let inFlight = false;
  // A trigger landing mid-flight (nav + decision ticks arrive within the same bar; an org switch while
  // fetching) must not be DROPPED — it queues one trailing reload, so bursts coalesce to exactly two
  // fetches and the tab never strands on stale data (or a false empty state) until the 30 s poll.
  let pendingReload = false;

  async function loadAll() {
    if (!strategyId) return;
    if (inFlight) {
      pendingReload = true;
      return;
    }
    inFlight = true;
    loading = true;
    const at = $selectedOrgId;
    const sid = strategyId;
    try {
      const s = await api.getStrategyIntegrity(sid, at);
      // Drop a stale response if the org or strategy moved while it was in flight (scoped-loader rule).
      if ($selectedOrgId !== at || strategy?.strategyId !== sid) return;
      snapshot = s;
      error = null;
    } catch (err) {
      // The scope check applies to failures too — a slow error from a superseded (org, strategy) must
      // not paint over the current scope's view.
      if ($selectedOrgId !== at || strategy?.strategyId !== sid) return;
      const msg = String(err?.message ?? err);
      error = msg.includes('status: 404')
        ? null
        : (msg.includes('details: ') ? msg.slice(msg.indexOf('details: ') + 'details: '.length) : msg);
    } finally {
      loading = false;
      inFlight = false;
      if (pendingReload) {
        pendingReload = false;
        loadAll();
      }
    }
  }

  // (Strategy, org) reload — guard seeded from raw stores, not `$:` values (lesson 2026-07-14).
  let lastKey = '';
  $: {
    const key = `${strategy?.strategyId ?? ''}|${$selectedOrgId ?? ''}`;
    if (key !== lastKey && strategy?.strategyId) {
      lastKey = key;
      snapshot = null;
      loadAll();
    }
  }

  let lastSeq = 0;
  $: if (isRunning && $navTickSeq !== lastSeq) {
    lastSeq = $navTickSeq;
    loadAll();
  }

  let lastDecisionSeq = 0;
  $: if (isRunning && $decisionTickSeq !== lastDecisionSeq) {
    lastDecisionSeq = $decisionTickSeq;
    loadAll();
  }

  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(loadAll, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });
  onMount(loadAll);

  function fmtAsOf(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString();
  }
  function fmtNav(v) {
    return v == null ? null : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function fmtFreq(seconds) {
    if (!seconds || seconds <= 0) return null;
    return seconds % 3600 === 0 ? `${seconds / 3600}h bars` : `${Math.round(seconds / 60)}m bars`;
  }
</script>

<div class="it-root">
  <div class="it-toolbar">
    {#if isRunning}<span class="it-live" title="Updating live on each NAV/decision tick">● live</span>{/if}
    {#if snapshot}
      <span class="it-asof num">
        as of {fmtAsOf(snapshot.asOf)}{#if fmtNav(snapshot.nav)} · NAV {fmtNav(snapshot.nav)}{/if}{#if fmtFreq(snapshot.frequencySeconds)} · {fmtFreq(snapshot.frequencySeconds)}{/if}{$selectedOrgId ? ' · org view' : ''}
      </span>
    {/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll} disabled={loading}>Refresh</button>
  </div>

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll}>Retry</button>
    </div>
  {:else if loading && !snapshot}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if !snapshot}
    <div class="empty-state">
      <p class="empty-state-description">
        No integrity data for this run{$selectedOrgId ? ' in the selected org' : ''} yet.
        {#if isRunning}Checks appear as the first bars and fills arrive.{/if}
      </p>
    </div>
  {:else}
    <ChecksStrip checks={snapshot.checks ?? []} />

    <section class="it-panel">
      <h4 class="it-panel-title">Book vs target</h4>
      <!-- The deadband comes ONLY from the server's assumptions (contract-guaranteed) — no client copy. -->
      <BookDriftTable book={snapshot.book ?? []} deadbandWeight={snapshot.ticketAssumptions?.deadbandWeight} />
    </section>

    <div class="it-grid">
      <section class="it-panel">
        <h4 class="it-panel-title">Rebalance ticket (advisory)</h4>
        <RebalanceTicket ticket={snapshot.ticket ?? []} assumptions={snapshot.ticketAssumptions} />
      </section>
      <section class="it-panel">
        <h4 class="it-panel-title">Cost accounting</h4>
        <CostLine cost={snapshot.cost} />
        <h4 class="it-panel-title it-panel-title-gap">Cadence</h4>
        <CadenceStrip cadence={snapshot.cadence} />
      </section>
    </div>

    {#if snapshot.shishito}
      <section class="it-panel">
        <h4 class="it-panel-title">Trailing-stop ladder (Shishito)</h4>
        <StopLadder shishito={snapshot.shishito} />
      </section>
    {/if}
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
  .it-root { display: flex; flex-direction: column; gap: 0.85rem; }
  .it-toolbar { display: flex; align-items: center; gap: 0.75rem; }
  .it-live { font-size: 0.75rem; color: var(--success, #1aa260); }
  .it-asof { font-size: 0.68rem; color: var(--text-muted, #8a8b8d); }
  .it-panel { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.75rem; }
  .it-panel-title { margin: 0 0 0.5rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  .it-panel-title-gap { margin-top: 0.85rem; }
  .it-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 0.85rem; align-items: start; }
  .num { font-variant-numeric: tabular-nums; }
</style>
